import {
  gridStringOrNumberComparator,
  gridDateComparator,
  GridComparatorFn,
} from '@mui/x-data-grid-pro'
import {
  EnhancedSortModes,
  type EnhancedSortRef,
} from './enhancedSort/EnhancedSortHOC'

export type SortOnProperty<T extends {}, v = any> = (arrayItem?: T | null) => v

/** assumes the array data is presorted for a given row column */
export function gridStringOrNumberArrayComparator<T extends {}>(
  /* if the cell type is an array of objects, you need to pass this function to select the property to sort on */
  selectSortProperty?: SortOnProperty<T, number | string | null | undefined>,
): GridComparatorFn<
  T[] | Array<string | number | null | undefined> | undefined | null
> {
  return (v1, v2, ...rest) =>
    gridStringOrNumberComparator(
      selectSortProperty?.(v1?.[0] as T) || v1?.[0],
      selectSortProperty?.(v2?.[0] as T) || v2?.[0],
      ...rest,
    )
}

/** assumes the array data is presorted for a given row column */
export function gridDateArrayComparator<T extends {}>(
  /* if the cell type is an array of objects, you need to pass this function to select the property to sort on */
  selectSortProperty?: SortOnProperty<T, Date | null>,
): GridComparatorFn<T[] | Array<Date | null | undefined> | null | undefined> {
  return (v1, v2, ...rest) =>
    gridDateComparator(
      selectSortProperty?.(v1?.[0] as T) || v1?.[0],
      selectSortProperty?.(v2?.[0] as T) || v2?.[0],
      ...rest,
    )
}

export function gridEnhancedSortComparator<T extends {}>(
  ref: EnhancedSortRef,
  selectSortProperty?: SortOnProperty<T>,
): GridComparatorFn<any[]> {
  const stringComp = gridStringOrNumberArrayComparator(selectSortProperty)
  const dateComp = gridDateArrayComparator(selectSortProperty)

  return (v1, v2, field1, field2) => {
    const mode = ref.current[field1.field]
    switch (mode) {
      case EnhancedSortModes.Count:
        return v1.length - v2.length
      case EnhancedSortModes.Number:
      case EnhancedSortModes.String:
        return stringComp(v1, v2, field1, field2)
      case EnhancedSortModes.Date:
        return dateComp(v1, v2, field1, field2)
      default:
        return -1
    }
  }
}
